import focus from '../../../../classes/focusController';
import store from '../../../../store';
import {
  TActionState,
  updateActionState,
} from '../../../../store/appStateSlice';
import propsStore from '../../../../store/propsStore/propsStore';
import { TApiaForm } from '../../../../types';
import debugDispatcher from '../../../../utils/debugDispatcher';
import { formatMessage } from '../../../../utils/util';
import {
  lockScreen,
  unlockScreen,
} from '../../../common/dialogs/modals/ScreenLock';
import { parseFakeJSON } from '../common/FieldLabel';
import formsEventsController from '../formsEvents/formsEventsController';
import {
  IValidationFieldError,
  TFireEvent,
  TGetValue,
  TInitialValueGetter,
  TSetError,
  TSetValue,
  TValidationFieldsErrorsCallback,
} from './types';

export type TFieldStoreMethod = () => Promise<boolean> | boolean;
export type TSynchronize = (configuration?: {
  additionalProps?: Record<string, unknown>;
  forced?: boolean;
}) => Promise<boolean>;

interface IFieldsStoreRegister {
  doFocus: () => void;
  doValidate: () => Promise<true | (() => void)>;
  doSynchronize: TSynchronize;
  fireEvent: TFireEvent;
  formData: TApiaForm;
  getInitialValue: TInitialValueGetter;
  getValue: TGetValue;
  id: string;
  isBusy: TFieldStoreMethod;
  markAsLoaded: () => void;
  setError: TSetError;
  setInitialValue: () => unknown;
  setValue: TSetValue;
}

declare global {
  interface Window {
    validate: () => unknown;
  }
}

const sortFieldsByPosition: (
  a: IFieldsStoreRegister,
  b: IFieldsStoreRegister,
) => number = (a, b) => {
  let aStore = propsStore.getFieldProps(a.id);
  let bStore = propsStore.getFieldProps(b.id);

  /** Primero se compara la posición de los formularios */
  const { sortedForms } = formsEventsController;

  const aFormPosition = sortedForms.indexOf(aStore.formData?.id ?? '');
  const bFormPosition = sortedForms.indexOf(bStore.formData?.id ?? '');

  if (aFormPosition !== bFormPosition) return aFormPosition - bFormPosition;

  /**
   * Si están en el mismo formulario, se determina si están en grilla o no. Si
   * están en grilla y están en la misma grilla, se compara su posición en la
   * grilla.
   */
  if (
    aStore.parentGridId === bStore.parentGridId &&
    aStore.parentGridId !== undefined &&
    bStore.parentGridId !== undefined
  ) {
    if (aStore.attributes.index !== bStore.attributes.index)
      return aStore.attributes.index - bStore.attributes.index;

    return (aStore.gridColumn as number) - (bStore.gridColumn as number);
  }

  /**
   * Si están en el mismo formulario pero no en la misma grilla, se compara su
   * posición en el formulario, o la posición de su grilla padre.
   */

  if (aStore.parentGridId)
    aStore = propsStore.getFieldProps(aStore.parentGridId);
  if (bStore.parentGridId)
    bStore = propsStore.getFieldProps(bStore.parentGridId);

  const ax = aStore?.field?.x ?? 1000;
  const ay = aStore?.field?.y ?? 1000;
  const bx = bStore?.field?.x ?? 1000;
  const by = bStore?.field?.y ?? 1000;

  if (ay !== by) return ay - by;
  return ax - bx;
};

let sortedFieldsStore: IFieldsStoreRegister[] | undefined;

const isDebugging = false;

/**
 * El fieldsStore es una clase auxiliar utilizada únicamente para sincronizar
 * los procesos de validación, sincronización de campos y ejecución de eventos
 * con el proceso de confirmación.
 * */
const fieldsStore = new (class FieldsStore {
  constructor() {
    debugDispatcher.on(
      'fieldsValues',
      () =>
        Object.values(this.fieldActionsContainer).forEach((field) =>
          console.log(field.id, field.getValue()),
        ),
      '',
    );
  }

  fieldActionsContainer: Record<string, IFieldsStoreRegister> = {};

  busyFields: string[] = [];

  actionState: TActionState | undefined = undefined;

  /**
   * Devuelve un mapa con métodos asociados al campo con el id pasada como
   * parámetro.
   *
   * @see IFieldsStoreRegister
   */
  getById(id: string) {
    return this.fieldActionsContainer[id];
  }

  markAsBusy(id: string) {
    if (this.busyFields.length === 0) lockScreen(true);
    this.busyFields.push(id);
  }

  markAsFree(id: string) {
    const previousBusyFields = this.busyFields.length;
    this.busyFields = this.busyFields.filter((el) => el !== id);
    if (this.busyFields.length === 0 && this.actionState) {
      updateActionState(this.actionState);
      this.actionState = null;
      if (previousBusyFields > 0) {
        unlockScreen();
      }
    } else if (this.busyFields.length === 0 && previousBusyFields > 0) {
      unlockScreen();
    }
  }

  #errorSuscribers: TValidationFieldsErrorsCallback[] = [];

  /**
   * Es posible suscribirse a los errores de validación con este método. Acepta
   * como único argumento un callback que recibirá un array compuesto de
   * objetos de tipo IValidationFieldError.
   *
   * **Devuelve un método que al ser invocado cancela la suscripción**
   */
  onValidateError(cb: TValidationFieldsErrorsCallback) {
    this.#errorSuscribers.push(cb);
    return () => {
      this.#errorSuscribers = this.#errorSuscribers.filter(
        (current) => current !== cb,
      );
    };
  }

  async synchronize() {
    const hasSynchronized = (
      await Promise.all(
        Object.values(this.fieldActionsContainer)
          .filter(
            (actions) =>
              !actions.formData.readOnly && !actions.formData.readonly,
          )
          .map((actions) => actions.doSynchronize()),
      )
    ).findIndex((result) => !result);
    if (isDebugging) console.log({ hasSynchronized });
    if (hasSynchronized !== -1) {
      console.log(Object.values(this.fieldActionsContainer)[hasSynchronized]);
      return false;
    }
    return true;
  }

  async validate() {
    const isBusy = this.busyFields.length > 0;
    if (isDebugging) console.log({ isBusy });
    if (isBusy) {
      this.actionState = store.getState().appStateSlice.actionState;
      updateActionState(null);
      return false;
    }

    sortedFieldsStore = Object.values(this.fieldActionsContainer).sort(
      sortFieldsByPosition,
    );

    const sortedFields = sortedFieldsStore;

    const errors: IValidationFieldError[] = [];

    const validationResult = await Promise.all(
      sortedFields.map((actions) => {
        const fieldProps = propsStore.getFieldProps(actions.id);

        const gridProps = propsStore.getFieldProps(
          actions.id.match(/^((E|P)_\d{3,5}_\d+)(?:_\d+_\d+)/)?.[1] as string,
        );

        const formProps = propsStore.getFieldProps(
          actions.id.match(/^((E|P)_\d{3,5})(?:_\d+)?/)?.[1] as string,
        );

        const isFieldHidden =
          fieldProps?.fieldType === 'hidden' ||
          fieldProps?.visibilityHidden ||
          gridProps?.visibilityHidden ||
          formProps?.frmHidden ||
          formProps?.visibilityHidden ||
          formProps?.readOnly;

        const value = actions.getValue();
        if (
          !fieldProps?.disabled &&
          !fieldProps?.inputAsText &&
          fieldProps?.required &&
          fieldProps.attId &&
          typeof value === 'string' &&
          (value.trim() === '' ||
            value.match(/^(?:<p>(?:&nbsp;|\s)*<\/p>)*$/)) &&
          !isFieldHidden
        ) {
          if (fieldProps.fieldType !== 'editor') {
            actions.setValue('');
          }

          actions.setError(formatMessage(window.MSG_REQ_FIELD, { TOK1: '' }));
          errors.push({
            doFocus: actions.doFocus,
            error: propsStore.getFieldProps(actions.id).message as string,
            label: propsStore.getFieldProps(actions.id).attributes.attLabel,
            id: actions.id,
          });
          return actions.doFocus;
        }

        return new Promise((resolve) => {
          try {
            const field = window.ApiaFunctions.getForm(
              formProps.formData.formName,
            )?.getFieldById(actions.id);
            if (!field) {
              resolve(true);
              fieldsStore.unregister(actions.id);
              return;
            }
          } catch (_) {
            resolve(true);
            fieldsStore.unregister(actions.id);
            return;
          }
          if (!fieldProps) {
            resolve(true);
            return;
          }

          if (!fieldProps || isFieldHidden) {
            resolve(true);
            return;
          }

          actions
            .doValidate()
            .then((hasValidated) => {
              if (hasValidated !== true) {
                errors.push({
                  doFocus: actions.doFocus,
                  error: propsStore.getFieldProps(actions.id).message as string,
                  label: propsStore.getFieldProps(actions.id).attributes
                    .attLabel,
                  id: actions.id,
                });
              }

              resolve(hasValidated);
            })
            .catch(() => {});
        });
      }),
    );

    const nonTranslatedFields = sortedFields.filter((current) => {
      const fieldId = current.id;
      const fieldProps = propsStore.getFieldProps(fieldId);

      if (!fieldProps) return false;

      const formProps = propsStore.getFieldProps(fieldProps.formData.id);
      const isFormReadonly = formProps.frmHidden || formProps.visibilityHidden;

      if (
        !isFormReadonly &&
        formProps.formData.langs &&
        ((fieldProps.reqTrad && fieldProps.value) ||
          (fieldProps.required && fieldProps.field.fieldType === 'file')) &&
        !fieldProps.isActuallyReadonly &&
        !fieldProps.disabled &&
        !errors.find((search) => search.id === current.id)
      ) {
        const formLangs = parseFakeJSON(formProps.formData?.langs ?? '') || [];

        const nonTranslatedLanguages =
          fieldProps.attributes.fieldType === 'file'
            ? formLangs.find((search) => {
                const translatedDocument = Object.values(
                  store.getState().fileUploadSlice.uploaders[fieldId]
                    .translatedFiles ?? {},
                )[0]?.[search.id];
                return !(
                  translatedDocument?.docName || translatedDocument?.name
                );
              })
            : store
                .getState()
                .translationSlice[fieldId]?.langs.filter((currentLang) => {
                  return currentLang.isTranslated === false;
                })[0];
        let currentError = '';
        if (
          nonTranslatedLanguages &&
          fieldProps.attributes.fieldType === 'file'
        ) {
          currentError = formatMessage(window.LBL_REQ_LANG, {
            TOK1: nonTranslatedLanguages.label,
          });
        } else if (nonTranslatedLanguages) {
          currentError = formatMessage(window.LBL_REQ_LANG, {
            TOK1: nonTranslatedLanguages?.label ?? 'NO LANGUAGE NAME',
          });
        }

        const willSetValidationError = !!currentError;

        if (willSetValidationError) {
          current.setError(currentError);
          errors.push({
            doFocus: current.doFocus,
            error: currentError,
            id: current.id,
            label: propsStore.getFieldProps(current.id).attributes.attLabel,
          });
        }

        return willSetValidationError;
      }
      return false;
    });

    if (errors.length > 0) {
      this.#errorSuscribers.forEach((cb) => cb(errors));
    }

    const notValidIndex = validationResult.findIndex(
      (result) => result !== true,
    );
    const hasValidated = notValidIndex === -1;
    if (isDebugging) console.log({ hasValidated });

    if (nonTranslatedFields.length > 0) {
      nonTranslatedFields[0].doFocus();
      return false;
    }
    if (!hasValidated) {
      (validationResult[notValidIndex] as () => true)();
      return false;
    }

    const hasSynchronized = await this.synchronize();
    if (hasSynchronized !== true)
      return 'Al menos un campo no pudo ser sincronizado';

    if (focus.notifications()) {
      if (isDebugging) console.log({ notifications: true });
      return false;
    }

    const { formSubmitAction } = store.getState().fieldEventsSlice;
    if (formSubmitAction) {
      if (isDebugging) console.log({ formSubmitAction });
      return false;
    }
    return true;
  }

  /**
   * @param id - Debe ser un identificador único de modo que si se necesita actualizar un parámetro en el registro,
   * pueda hacerse sin complicaciones. (La función register puede ser llamada múltiples veces para el mismo campo,
   * atendiendo las necesidades de actualizar los callbacks que tienen dependencias en el uso de useCallback por ejemplo).
   *
   * @param doSynchronize - La función doSynchronize debe devolver una promesa que resuelva en true o false,
   * debe resolver a true si se pudo sincronizar y a false sino.
   *
   * @param doValidate - La función doValidate debe devolver una promesa que resuelva en true o false,
   * debe resolver a true si pudo validar y a false sino.
   *
   * @param isBusy - La función isBusy idealmente devolverá siempre false, pero únicamente una vez que el campo se haya
   * desocupado (haya terminado con la ejecución de eventos), de modo que permita continuar con los procesos pertinentes
   * únicamente cuando los procesos hayan terminado.
   */
  register(fieldActions: IFieldsStoreRegister) {
    this.fieldActionsContainer[fieldActions.id] = fieldActions;
  }

  unregister(fieldId: string) {
    delete this.fieldActionsContainer[fieldId];
    propsStore.removeField(fieldId);
  }
})();

window.validate = () => fieldsStore.validate();

export default fieldsStore;
